import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import classnames from 'classnames';

import { Option } from 'types/options';
import { Routes } from 'types/routes';

import { Types } from 'models/CartItem';
import Product from 'models/Product';

import { getOptionValue } from 'utils/option';
import { withVariables } from 'utils/string';

import LocaleContext from 'components/context/Locale';
import Button, { ButtonElements, ButtonStyles, ButtonVariants } from 'components/layout/Button';
import ProductSelect from 'components/layout/ProductSelect';
import AddToCartButton from 'components/modules/public/products/AddToCartButton';

import RenderSilhouette from './modules/renderSilhouette';
import { BodyPart, Parts, Side } from './modules/renderSilhouette/component';
import { Props } from './index';
import { initialBodyPartsWoman } from './initialData';
import StyledComponent from './styles';

export interface RenderedBodyPart {
    name: BodyPart<Parts>;
    active: Boolean;
    product?: Product;
    localeName: string;
    side: 'right' | 'left';
}

export default function PublicInteractiveBodyManComponent({ state, actions, buttonProps, products }: Props): JSX.Element {
    const localeContext = useContext(LocaleContext);
    const translations = localeContext?.translations?.blockSectionProducts;
    const [isZoomedIn, setZoom] = useState<Boolean>(false);
    const [productView, setProductView] = useState<Boolean>(false);
    const [selectedBodyPart, setSelectedBodyPart] = useState<RenderedBodyPart>(null);
    const [currentPricingMatrixOption, setCurrentPricingMatrixOption] = useState<Option<number>>(null);
    const [bodyParts, setBodyParts] = useState<RenderedBodyPart[]>(initialBodyPartsWoman);
    const router = useRouter();

    useEffect(() => {
        if (products?.elements?.length > 0) {
            const nextBodyParts = [...bodyParts];
            nextBodyParts.map((nextBodyPart, nextBodyPartIndex) => {
                products.elements.map(apiProduct => {
                    const productBodyPartNames = apiProduct.productBodyParts.map(productBodyPart => productBodyPart.bodyPart.name);
                    if (productBodyPartNames.includes(nextBodyPart.localeName)) {
                        nextBodyParts[nextBodyPartIndex].product = apiProduct;
                    }
                });
            });

            setBodyParts(nextBodyParts);
        }
    }, []);

    const onCreateCartOffer = async (id: string) => {
        return await actions.createCartItem({
            cartId: state?.cart?.id,
            itemId: id,
            itemType: Types.Product,
            quantity: getOptionValue(currentPricingMatrixOption) || 1,
        });
    };

    useEffect(() => {
        const bodyPartQuery = router.query.bodyPart;

        if (bodyPartQuery) {
            const getMatchedBodyPart = bodyParts.find(part => part.localeName === bodyPartQuery);
            setProductView(true);
            setZoom(true);
            setSelectedBodyPart(getMatchedBodyPart);
        }
    }, []);

    const onClickEventHandler = (bodyPart: RenderedBodyPart) => {
        setProductView(!productView);

        const definedBodyPart = productView ? null : bodyPart;
        setSelectedBodyPart(definedBodyPart);
        router.push(router.pathname, `${router.pathname}?bodyPart=${bodyPart.localeName}`, { shallow: true });

        setZoom(!isZoomedIn);
    };

    const renderController = (bodyPart: RenderedBodyPart): JSX.Element => {
        const ViewLayer = (): JSX.Element => {
            return (
                <div
                    className="control circled-cross"
                    id={bodyPart.name}
                />
            );
        };

        const EventHandlerLayer = (props: any): JSX.Element => {
            return (
                <div
                    className="event-handler-layer"
                    onClick={() => onClickEventHandler(bodyPart)}
                >
                    {props.children}
                </div>
            );
        };


        const compose = (): JSX.Element => {
            return (
                <EventHandlerLayer>
                    <ViewLayer />
                </EventHandlerLayer>
            );
        };

        return compose();
    };

    const handleOnMouseEvent = (bodyPart: RenderedBodyPart | null, side: BodyPart<Side>) => {
        if (!productView && bodyPart) {
            setSelectedBodyPart({ ...bodyPart, side } as RenderedBodyPart);
        }
    };

    useEffect(() => {
        let timeout: number;

        if (!isZoomedIn) {
            timeout = window.setTimeout(() => {
                setSelectedBodyPart(null);
                router.push(router.pathname, router.pathname, { shallow: true });
            }, 100);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [isZoomedIn]);

    const packagePriceDisplay = selectedBodyPart && selectedBodyPart?.product?.pricing?.priceDisplay;
    const packageOnGoingPromoPriceDisplay = packagePriceDisplay && selectedBodyPart.product.ongoingPromo && selectedBodyPart.product.ongoingPromo.pricing.priceDisplay;

    return (
        <StyledComponent className={classnames(
            'public-interactive-body-woman-component',
            productView && 'product-view'
        )}
        >
            <div
                className={classnames(
                    'zoom-wrapper',
                    productView && 'zoom-in',
                    (productView && selectedBodyPart) || null
                )}
                onClick={() => {
                    if (isZoomedIn) setZoom(false);
                    if (productView) setProductView(false);
                }}
            >
                <div className="body-base">
                    <RenderSilhouette
                        bodyPart={selectedBodyPart?.name}
                        zoomIn={isZoomedIn}
                    />

                    {!isZoomedIn ? (
                        <div className="columns-container">
                            <div className="hitboxes-container column-left">
                                {bodyParts
                                    .filter(bodyPart => bodyPart.side === 'left')
                                    .filter(bodyPart => bodyPart.product)
                                    .map((bodyPart) => (
                                        <div
                                            key={bodyPart.name}
                                            className={classnames(
                                                'hitbox',
                                                (productView && selectedBodyPart) && 'active' || null
                                            )}
                                            onClick={() => onClickEventHandler(bodyPart)}
                                            onMouseEnter={() => handleOnMouseEvent(bodyPart, Side.Left)}
                                            onMouseLeave={() => setSelectedBodyPart(null)}
                                            id={bodyPart.name}
                                        >
                                            <div className="controls-layer">
                                                {renderController(bodyPart)}
                                                {(selectedBodyPart && selectedBodyPart.side === Side.Left && selectedBodyPart.name === bodyPart.name) && (
                                                    <div className="hitbox__tooltip left">
                                                        {selectedBodyPart?.product?.locale?.name}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                            <div className="hitboxes-container column-right">
                                {bodyParts
                                    .filter(bodyPart => bodyPart.side === 'right')
                                    .filter(bodyPart => bodyPart.product)
                                    .map((bodyPart) => (
                                        <div
                                            key={bodyPart.name}
                                            className={classnames(
                                                'hitbox',
                                                (productView && selectedBodyPart) && 'active' || null
                                            )}
                                            onClick={() => onClickEventHandler(bodyPart)}
                                            onMouseEnter={() => handleOnMouseEvent(bodyPart, Side.Right)}
                                            onMouseLeave={() => setSelectedBodyPart(null)}
                                            id={bodyPart.name}
                                        >
                                            <div className="controls-layer">
                                                {renderController(bodyPart)}
                                                {(selectedBodyPart && selectedBodyPart.side === Side.Right && selectedBodyPart.name === bodyPart.name) && (
                                                    <div className="hitbox__tooltip right">
                                                        {selectedBodyPart?.product?.locale?.name}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ) : (
                        <div className="zoom-out-controller">
                            <Button
                                {...buttonProps?.undo}
                            />
                        </div>
                    )}
                </div>
            </div>
            {productView && (
                <div className="offer-details">
                    {selectedBodyPart?.product && (
                        <div className="content-container">
                            <div className="header">
                                {selectedBodyPart?.product?.locale?.name && (
                                    <div
                                        className="headline"
                                        dangerouslySetInnerHTML={{ __html: selectedBodyPart?.product?.locale?.name }}
                                    />
                                )}
                                <div className="column">
                                    {selectedBodyPart?.product?.showProductDuration && (
                                        <div className="offer-duration-container">
                                            {translations?.product?.productDurationTo}
                                            <span>
                                                {selectedBodyPart?.product?.productDurationToDisplay?.day}
                                                {' '}
                                                {translations?.product?.to}
                                                {' '}
                                                {selectedBodyPart?.product?.productDurationToDisplay?.hour}
                                            </span>
                                        </div>
                                    )}
                                    {selectedBodyPart?.product?.showLowestPrice && (
                                        <div className="last-month-price-container">
                                            <div className="info-mark">
                                                i
                                                <div className="info-mark__information">
                                                    {translations?.product?.tooltipInfo}
                                                </div>
                                            </div>
                                            {translations?.product?.lowestPrice}
                                            <span>
                                                {selectedBodyPart?.product?.displayLowestPriceValue}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="price-data-container">
                                    <div className="prices-container">
                                        {(!selectedBodyPart?.product?.ongoingPromo || (selectedBodyPart?.product?.ongoingPromo && selectedBodyPart?.product?.promoPercent <= 0)) && (
                                            <div
                                                className="pricing-price"
                                                dangerouslySetInnerHTML={{ __html: packagePriceDisplay || selectedBodyPart?.product?.defaultPricingMatrix?.priceDisplay }}
                                            />
                                        )}
                                        {(selectedBodyPart?.product?.ongoingPromo && selectedBodyPart?.product?.promoPercent > 0) && (
                                            <>
                                                <div
                                                    className="pricing-price"
                                                    dangerouslySetInnerHTML={{ __html: packageOnGoingPromoPriceDisplay || selectedBodyPart?.product?.defaultOngoingPromoPricingMatrix?.priceDisplay }}
                                                />
                                                <div
                                                    className="pricing-old-price"
                                                    dangerouslySetInnerHTML={{ __html: packagePriceDisplay || selectedBodyPart?.product?.defaultPricingMatrix?.priceDisplay }}
                                                />
                                            </>
                                        )}
                                    </div>
                                    {(selectedBodyPart?.product?.ongoingPromo && selectedBodyPart?.product?.promoPercent > 0) && (
                                        <div className="discount">
                                            -{selectedBodyPart?.product?.promoPercent}%
                                        </div>
                                    )}
                                </div>
                                {selectedBodyPart?.product?.locale?.description && (
                                    <div
                                        className="description"
                                        dangerouslySetInnerHTML={{ __html: selectedBodyPart?.product?.locale?.description }}
                                    />
                                )}
                            </div>

                            <div className="footer">
                                <div className="footer-controls-state">
                                    {selectedBodyPart?.product?.finalPricingMatrixOptions.length > 0 && (
                                        <ProductSelect
                                            options={selectedBodyPart?.product?.finalPricingMatrixOptions}
                                            onChange={(selectedOption: Option<number>) => setCurrentPricingMatrixOption(selectedOption)}
                                            value={currentPricingMatrixOption}
                                            cartClick={() => onCreateCartOffer(selectedBodyPart?.product?.id)}
                                            selectOptions={{ buyNowButton: true, deselect: true }}
                                        />
                                    )}

                                    <AddToCartButton
                                        onClick={() => onCreateCartOffer(selectedBodyPart?.product?.id)}
                                    />
                                </div>
                                <div className="footer-controls">
                                    <Button
                                        className="control"
                                        style={ButtonStyles.Gold}
                                        variant={ButtonVariants.Underline}
                                        element={ButtonElements.Anchor}
                                        href={withVariables(Routes.PublicProduct, {
                                            genderZoneSlug: selectedBodyPart?.product?.genderZoneSlug,
                                            slug: selectedBodyPart?.product?.locale?.slug,
                                        })}
                                        {...buttonProps?.more}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </StyledComponent>
    );
}
